import { IoClose } from '@react-icons/all-files/io5/IoClose'
import { IconButton, Popover, Text } from '@radix-ui/themes'
import clsx from 'clsx'
import React from 'react'

import styles from './popover-with-close.module.css'

type PopoverContentProps = React.ComponentProps<typeof Popover.Content>

type Props = {
  title?: string
  content: React.ReactNode
  children: React.ReactNode
  className?: string
  side?: PopoverContentProps['side']
  align?: PopoverContentProps['align']
  sideOffset?: PopoverContentProps['sideOffset']
  width?: PopoverContentProps['width']
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

/**
 * Popover with a title header and built-in close (X) button.
 *
 * Wraps `@radix-ui/themes` Popover. Pass the trigger as children; `title` renders
 * in the header alongside a close IconButton, and `content` renders as the body.
 *
 * @see https://www.radix-ui.com/themes/docs/components/popover
 */
export const PopoverWithClose = ({
  title,
  content,
  children,
  className,
  side,
  align,
  sideOffset,
  width,
  open,
  onOpenChange,
}: Props) => {
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content
        className={clsx(styles.content, className)}
        side={side}
        align={align}
        sideOffset={sideOffset}
        width={width}
      >
        <Popover.Close className={styles.closeButton}>
          <IconButton size="1" variant="ghost" color="gray" aria-label="Close">
            <IoClose />
          </IconButton>
        </Popover.Close>
        {title && (
          <Text size="2" weight="bold" mb="3" as="p">
            {title}
          </Text>
        )}
        {content}
      </Popover.Content>
    </Popover.Root>
  )
}
